import { create } from "zustand";
import { nanoid } from "nanoid";
import { patrolDb, type ScanDraft } from "../lib/db";
import { postScan, type ScanPayload } from "../services/api";

interface OfflineQueueState {
  initialized: boolean;
  drafts: ScanDraft[];
  syncing: boolean;
  enqueue: (payload: Omit<ScanDraft, "id" | "createdAt" | "retries">) => Promise<ScanDraft>;
  remove: (id: string) => Promise<void>;
  sync: () => Promise<void>;
  load: () => Promise<void>;
}

export const useOfflineQueue = create<OfflineQueueState>((set, get) => ({
  initialized: false,
  drafts: [],
  syncing: false,
  async load() {
    const drafts = await patrolDb.scanDrafts.orderBy("createdAt").toArray();
    drafts.forEach((draft) => {
      if (draft.online === undefined) {
        draft.online = false;
      }
    });
    set({ drafts, initialized: true });
  },
  async enqueue(payload) {
    const draft: ScanDraft = {
      ...payload,
      id: nanoid(),
      createdAt: new Date().toISOString(),
      retries: 0,
      online: payload.online ?? false
    };
    await patrolDb.scanDrafts.put(draft);
    set((state) => ({ drafts: [...state.drafts, draft] }));
    await patrolDb.syncLogs.add({
      status: "pending",
      message: `Queued scan for checkpoint ${draft.checkpointId}`,
      createdAt: new Date().toISOString()
    });
    return draft;
  },
  async remove(id) {
    await patrolDb.scanDrafts.delete(id);
    set((state) => ({ drafts: state.drafts.filter((draft) => draft.id !== id) }));
  },
  async sync() {
    if (get().syncing) return;
    set({ syncing: true });
    try {
      const drafts = await patrolDb.scanDrafts.orderBy("createdAt").toArray();
      for (const draft of drafts) {
        try {
          const payload: ScanPayload = {
            token: draft.token,
            clientTs: draft.clientTs,
            lat: draft.lat,
            lng: draft.lng,
            accuracy: draft.accuracy,
            notes: draft.notes,
            photo: draft.photoDataUrl,
            online: draft.online
          };

          await postScan(payload);

          await patrolDb.scanDrafts.delete(draft.id);
          await patrolDb.syncLogs.add({
            status: "success",
            message: `Synced checkpoint ${draft.checkpointId}`,
            createdAt: new Date().toISOString()
          });
        } catch (error) {
          await patrolDb.scanDrafts.update(draft.id, {
            retries: draft.retries + 1
          });
          await patrolDb.syncLogs.add({
            status: "error",
            message: error instanceof Error ? error.message : "Sync failed",
            createdAt: new Date().toISOString()
          });
        }
      }

      set({ drafts: await patrolDb.scanDrafts.orderBy("createdAt").toArray() });
    } finally {
      set({ syncing: false });
    }
  }
}));
